import { Injectable } from '@angular/core';
import { OrgAlertHistoryItem, OrgAlertHistoryRequest } from '../patient.model';

const STORAGE_KEY_PREFIX = 'afas:org-alert-history:';

export type CachedAlertHistoryFilters = Omit<
  OrgAlertHistoryRequest,
  'nextPage' | 'pageSize' | 'status'
>;

export type CachedAlertHistoryState = {
  campaignId: string;
  dateStart: string | null;
  dateEnd: string | null;
  search: string;
  activeFilters: CachedAlertHistoryFilters | null;
  alerts: OrgAlertHistoryItem[];
  totalCount: number | null;
  pageIndex: number;
  hasNextPage: boolean;
  hasRun: boolean;
};

@Injectable({ providedIn: 'root' })
export class OrgAlertHistoryCacheService {
  #memory = new Map<string, CachedAlertHistoryState>();

  load(orgId: string | undefined): CachedAlertHistoryState | null {
    if (!orgId) {
      return null;
    }
    const fromMemory = this.#memory.get(orgId);
    if (fromMemory) {
      return fromMemory;
    }
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_PREFIX + orgId);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as CachedAlertHistoryState;
      this.#memory.set(orgId, parsed);
      return parsed;
    } catch {
      return null;
    }
  }

  save(orgId: string | undefined, state: CachedAlertHistoryState): void {
    if (!orgId) {
      return;
    }
    this.#memory.set(orgId, state);
    try {
      sessionStorage.setItem(STORAGE_KEY_PREFIX + orgId, JSON.stringify(state));
    } catch {
      // Ignore quota / private mode errors; in-memory cache still works for navigation.
    }
  }

  clear(orgId: string | undefined): void {
    if (!orgId) {
      return;
    }
    this.#memory.delete(orgId);
    try {
      sessionStorage.removeItem(STORAGE_KEY_PREFIX + orgId);
    } catch {
      // no-op
    }
  }
}
